import { v1 as uuid } from 'uuid';

/**
 * Flattens a nested object into a single level object.
 * @param obj - The nested object.
 * @returns {T} - The flattened object.
 */
export const flatten = <T>(obj: T | { [key: string]: Partial<T> } = {}) => {
  return Object.keys(obj).reduce((acc, key) => {
    const value = obj[key];
    if (
      Object.prototype.toString.call(value) === '[object Object]' &&
      value !== null
    ) {
      return { ...acc, ...flatten(value) };
    }
    return {
      ...acc,
      [key]: isNilOrEmpty(value) ? '' : `${value}`,
    };
  }, {});
};

/**
 * Creates a diff object given two objects of the same type.
 * @param obj1 - The first object.
 * @param obj2 - The second object.
 * @returns {Partial<T>} - The diff object containing only the properties that have different values.
 */
export function diff<T>(obj1: T, obj2: T): Partial<T> {
  const diff: Partial<T> = {};
  Object.keys(obj1).forEach((key) => {
    if (obj1[key] !== obj2[key]) {
      diff[key] = obj1[key];
    }
  });
  return diff;
}

/**
 * Merges a Datalayer[] in a Datalayer single object
 * @returns {Datalayer}
 */
export function mergeLayers<T>(layers: T[]): T {
  if (!layers || !layers.length) {
    return {} as T;
  }
  return layers.reduce((acc, layer) => {
    Object.keys(layer).forEach((key) => {
      acc[key] = layer[key];
    });
    return acc;
  }, {} as T);
}

/**
 * Generates a unique conversion identifier.
 * The identifier is composed of a UUID and the current timestamp, separated by a dot.
 * @returns {string} A unique conversion identifier in the format `UUID.timestamp`.
 */
export const getConvId = () => {
  return `${uuid()}.${new Date().getTime()}`;
};

/**
 * Checks if a value is null, undefined, an empty string, or NaN (for numbers).
 * @param value - The value to check.
 * @returns {boolean} - True if the value is null, undefined, an empty string, or NaN; otherwise, false.
 */
export const isNilOrEmpty = (
  value: string | number | boolean | null | undefined
): boolean => {
  return (
    value == null || // Covers both null and undefined
    (typeof value === 'string' && value.trim() === '') ||
    (typeof value === 'number' && isNaN(value))
  );
};

/**
 * Creates an empty object with keys from the provided array, initializing each key with an empty string.
 * This is useful for creating a structured object where all values are initially empty.
 * @param keys - An array of strings representing the keys for the object.
 * @template T - The type of the object to be created.
 * @returns T - An object of type T with keys from the array and empty string values.
 */
export const createEmptyObjectFromArray = <T extends Record<string, string>>(
  keys: Array<string>
): T => {
  return keys.reduce(
    (acc, item) => ({
      ...acc,
      [item]: '',
    }),
    {} as T
  );
};
